import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { legacyApi, v1Api, handleApiError } from '../services/ecommerceApi';
import { Link } from 'react-router-dom';
import {
  FiShoppingBag, FiTruck, FiCheckCircle, FiMessageCircle,
  FiRefreshCw, FiSend, FiArrowLeft, FiTrendingUp, FiDollarSign,
  FiPlus, FiSettings, FiBarChart, FiEdit2, FiTrash2, FiBox, FiXCircle, FiUser,
} from 'react-icons/fi';
import ACTION_LABELS from './actionLabels';
import WhatsAppChatFragment from './WhatsAppChatFragment';
import '../arcam-ecommerce.css';

const E164_REGEX = /^\+[1-9]\d{1,14}$/;
const isValidE164 = (phone) => typeof phone === 'string' && E164_REGEX.test(phone);
const generateUUID = () => (window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
const STATUS_COLORS = {
  CONFIRMED: 'bg-blue-100 text-blue-700 border-blue-200',
  PACKED: 'bg-amber-100 text-amber-700 border-amber-200',
  SHIPPED: 'bg-purple-100 text-purple-700 border-purple-200',
  DELIVERED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  CANCELLED: 'bg-red-100 text-red-700 border-red-200',
  RETURNED: 'bg-orange-100 text-orange-700 border-red-200',
};




function VendorLogin({ onLogin }) {
  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!apiKey.trim()) {
      toast.error('Please enter your vendor API key');
      return;
    }
    setLoading(true);
    try {
      const res = await legacyApi.post(`/vendor/login`, { apiKey: apiKey.trim() });
      onLogin(res.data);
      toast.success(`Welcome, ${res.data.name}!`);
    } catch (e) {
      toast.error(e.message || 'Invalid API key');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl border border-neutral-200 shadow-lg p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-emerald-600 flex items-center justify-center mx-auto mb-4">
              <FiShoppingBag className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-neutral-900">Vendor Portal</h1>
            <p className="text-sm text-neutral-500 mt-1">Enter your API key to manage your store</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Vendor API Key</label>
              <input
                type="password"
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                placeholder="vk_xxxxxxxxxxxxxxxxxxxxxxxx"
                onKeyDown={e => e.key === 'Enter' && handleLogin()}
                className="w-full px-4 py-2.5 rounded-lg border border-neutral-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-200"
                autoFocus
              />
            </div>

            <button
              onClick={handleLogin}
              disabled={loading}
              className="w-full py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {loading ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiSettings className="w-4 h-4" />}
              Login to Vendor Portal
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-neutral-100">
            <p className="text-xs text-neutral-400 text-center">
              Don't have an API key? Contact your platform administrator to get one.
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link to="/" className="text-xs text-neutral-400 hover:text-neutral-600">
              &larr; Back to WhatsApp
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function DashboardTab({ vendor, onSelectRecord }) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadDashboard = useCallback(async () => {
    if (!vendor) return;
    setLoading(true);
    try {
      const res = await legacyApi.get(`/dashboard`, { headers: { 'x-vendor-api-key': vendor.api_key } });
      setDashboard(res.data);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [vendor]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  if (!vendor) {
    return <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-8 text-center text-neutral-400">Select a vendor to view dashboard.</div>;
  }

  if (loading) {
    return <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-8 text-center text-neutral-400">Loading dashboard...</div>;
  }

  if (!dashboard) {
    return <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-8 text-center text-neutral-400">No dashboard data available.</div>;
  }

  const { stats, recentOrders, recentWholesale } = dashboard;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="arcam-stat-card bg-white rounded-xl border border-neutral-200 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <FiShoppingBag className="w-4 h-4" style={{ color: 'var(--arcam-gold)' }} />
            <span className="arcam-stat-label text-xs font-medium text-neutral-500">Total Orders</span>
          </div>
          <p className="arcam-stat-value text-2xl font-bold text-neutral-900">{stats.orders?.total || 0}</p>
          <p className="arcam-stat-sub text-xs text-neutral-400 mt-1">Revenue: {stats.orders?.revenue || 0}</p>
        </div>
        <div className="arcam-stat-card bg-white rounded-xl border border-neutral-200 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <FiTruck className="w-4 h-4" style={{ color: 'var(--arcam-gold)' }} />
            <span className="arcam-stat-label text-xs font-medium text-neutral-500">Shipped</span>
          </div>
          <p className="arcam-stat-value text-2xl font-bold text-neutral-900">{stats.orders?.shipped || 0}</p>
          <p className="arcam-stat-sub text-xs text-neutral-400 mt-1">Delivered: {stats.orders?.delivered || 0}</p>
        </div>
        <div className="arcam-stat-card bg-white rounded-xl border border-neutral-200 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <FiTrendingUp className="w-4 h-4" style={{ color: 'var(--arcam-gold)' }} />
            <span className="arcam-stat-label text-xs font-medium text-neutral-500">Campaigns</span>
          </div>
          <p className="arcam-stat-value text-2xl font-bold text-neutral-900">{stats.campaigns?.total || 0}</p>
          <p className="arcam-stat-sub text-xs text-neutral-400 mt-1">Sent: {stats.campaigns?.sent || 0}</p>
        </div>
        <div className="arcam-stat-card bg-white rounded-xl border border-neutral-200 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <FiDollarSign className="w-4 h-4" style={{ color: 'var(--arcam-gold)' }} />
            <span className="arcam-stat-label text-xs font-medium text-neutral-500">Wholesale</span>
          </div>
          <p className="arcam-stat-value text-2xl font-bold text-neutral-900">{stats.wholesale?.total || 0}</p>
          <p className="arcam-stat-sub text-xs text-neutral-400 mt-1">Quotations: {stats.wholesale?.quotation_ready || 0}</p>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="arcam-card bg-white rounded-xl border border-neutral-200 shadow-sm p-6">
        <h4 className="arcam-card-title text-lg font-bold text-neutral-900 mb-4 flex items-center gap-2">Recent Orders</h4>
        {recentOrders?.length === 0 ? (
          <p className="text-sm text-neutral-400">No orders yet.</p>
        ) : (
          <div className="space-y-2">
            {recentOrders?.map(o => (
              <div
                key={o.id}
                onClick={() => onSelectRecord({ type: 'order', ref: o.order_ref, id: o.id })}
                className="flex items-center justify-between py-2 border-b border-neutral-100 last:border-0 cursor-pointer hover:bg-neutral-50 transition-colors"
              >
                <div>
                  <p className="text-sm font-medium text-neutral-900">{o.order_ref}</p>
                  <p className="text-xs text-neutral-500">{o.customer_name} · {o.customer_phone}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-neutral-900">{o.total_amount} {o.currency}</span>
                  <span className={`arcam-status-badge px-2 py-0.5 rounded-full text-xs font-semibold border arcam-status--${o.status} ${STATUS_COLORS[o.status]}`}>{o.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Wholesale */}
      <div className="arcam-card bg-white rounded-xl border border-neutral-200 shadow-sm p-6">
        <h4 className="arcam-card-title text-lg font-bold text-neutral-900 mb-4 flex items-center gap-2">Recent Wholesale Requests</h4>
        {recentWholesale?.length === 0 ? (
          <p className="text-sm text-neutral-400">No wholesale requests yet.</p>
        ) : (
          <div className="space-y-2">
            {recentWholesale?.map(r => (
              <div
                key={r.id}
                onClick={() => onSelectRecord({ type: 'wholesale', ref: r.request_ref, id: r.id })}
                className="flex items-center justify-between py-2 border-b border-neutral-100 last:border-0 cursor-pointer hover:bg-neutral-50 transition-colors"
              >
                <div>
                  <p className="text-sm font-medium text-neutral-900">{r.request_ref}</p>
                  <p className="text-xs text-neutral-500">{r.product_name} · Qty: {r.quantity}</p>
                </div>
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-neutral-100 text-neutral-700">{r.status.replace(/_/g, ' ')}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function OrderFlowTab({ vendor, selectedRecord }) {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [phone, setPhone] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [order, setOrder] = useState(null);
  const [chat, setChat] = useState([]);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [source, setSource] = useState('manual');
  const [countdown, setCountdown] = useState(0);
  const [isCounting, setIsCounting] = useState(false);
  const [autoFillSource, setAutoFillSource] = useState(null);
  const [recordLoading, setRecordLoading] = useState(false);
  const [pendingProductId, setPendingProductId] = useState(null);
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const chatEndRef = useRef(null);

  const headers = useMemo(() => vendor ? { 'x-vendor-api-key': vendor.api_key } : {}, [vendor]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat]);

  useEffect(() => {
    setLoadingProducts(true);
    legacyApi.get(`/products`, { headers })
      .then(res => { setProducts(res.data); setLoadingProducts(false); })
      .catch((e) => { setLoadingProducts(false); handleApiError(e); });
  }, [headers]);

  useEffect(() => {
    if (pendingProductId != null) {
      const product = products.find(p => p.id === pendingProductId);
      if (product) {
        setSelectedProduct(product);
        setPendingProductId(null);
      }
    }
  }, [products, pendingProductId]);

  useEffect(() => {
    if (source === 'manual') {
      setRecentCustomers([]);
      setRecentOrders([]);
      return;
    }
    setRecordLoading(true);
    const endpoint = source === 'customer' ? '/customers' : '/orders';
    legacyApi.get(endpoint, { headers })
      .then(res => source === 'customer' ? setRecentCustomers(res.data) : setRecentOrders(res.data))
      .catch(e => toast.error(e.response?.data?.error || e.message))
      .finally(() => setRecordLoading(false));
  }, [source, headers]);

  const handleSelectRecord = (record) => {
    setAutoFillSource(null);
    if (record.type === 'order' || record.order_ref) {
      loadFromOrder(record.ref || record.order_ref);
    } else {
      loadFromCustomer(record.id);
    }
  };

  const handleSourceChange = (newSource) => {
    setSource(newSource);
    setAutoFillSource(null);
    if (newSource === 'manual') {
      setCustomerName('');
      setPhone('');
      setShippingAddress('');
      setSelectedProduct(null);
      setConsent(false);
      setPendingProductId(null);
    }
  };

  const loadFromCustomer = async (customerId) => {
    setRecordLoading(true);
    try {
      const res = await legacyApi.get(`/customers/${customerId}/orders`, { headers });
      const data = res.data;
      const customer = data.customer || {};
      setCustomerName(customer.name || '');
      setPhone(customer.phone_e164 || '');
      setShippingAddress(data.latestAddress || customer.default_shipping_address || '');
      setConsent(!!(customer.preference?.marketingOptIn));

      const firstItem = data.latestOrder?.items?.[0];
      if (firstItem) {
        setPendingProductId(firstItem.product_id);
        setSelectedProduct(null);
      } else {
        setPendingProductId(null);
        setSelectedProduct(null);
      }
      setAutoFillSource(`customer ${customer.name || customer.phone_e164}`);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setRecordLoading(false);
    }
  };

  const loadFromOrder = async (orderRef) => {
    setRecordLoading(true);
    try {
      const res = await legacyApi.get(`/orders/${orderRef}?include=customer,items`, { headers });
      const data = res.data;
      const customer = data.customer || {};
      setCustomerName(customer.name || data.customer_name || '');
      setPhone(customer.phone_e164 || data.customer_phone || '');
      setShippingAddress(data.shipping_address || '');
      setConsent(data.consent ? true : !!(data.preference?.marketingOptIn));
      const firstItem = data.items?.[0];
      if (firstItem) {
        const product = products.find(p => p.id === firstItem.product_id);
        if (product) {
          setSelectedProduct(product);
          setPendingProductId(null);
        } else {
          setPendingProductId(firstItem.product_id);
        }
      } else {
        setSelectedProduct(null);
        setPendingProductId(null);
      }
      setAutoFillSource(`order ${data.order_ref}`);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setRecordLoading(false);
    }
  };

  useEffect(() => {
    if (selectedRecord?.ref) {
      setOrder(null);
      setChat([]);
      setSource(selectedRecord.type === 'order' ? 'order' : 'customer');
      if (selectedRecord.type === 'order') {
        loadFromOrder(selectedRecord.ref);
      } else {
        loadFromCustomer(selectedRecord.id);
      }
    }
  }, [selectedRecord]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    if (countdown === 0 && isCounting) {
      setIsCounting(false);
      placeOrder();
    }
  }, [countdown, isCounting]); // eslint-disable-line react-hooks/exhaustive-deps

  const addBotMessage = useCallback((text, buttons = []) => {
    setChat(prev => [...prev, {
      direction: 'out',
      text,
      buttons: buttons.map(b => ({ ...b, onClick: () => handleActionRef.current(b.id) })),
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    }]);
  }, []);

  const addUserMessage = useCallback((text) => {
    setChat(prev => [...prev, {
      direction: 'in',
      text,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    }]);
  }, []);

  const seenInboundIdsRef = useRef(new Set());
  const seenOutboundIdsRef = useRef(new Set());
  const chatClearedAtRef = useRef(null);
  useEffect(() => {
    if (!phone) return;
    seenInboundIdsRef.current = new Set();
    seenOutboundIdsRef.current = new Set();
    chatClearedAtRef.current = null;
    const interval = setInterval(async () => {
      try {
        const { data } = await axios.get('/api/whatsapp/messages', {
          params: { phone, limit: 50 },
          headers,
        });
        const rows = Array.isArray(data) ? data : (data.rows || []);
        for (const row of rows) {
          const id = row.wa_message_id || row.id;
          if (!id) continue;
          const text = String(row.body || '').trim();
          if (!text) continue;

          const rowTime = row.created_at ? new Date(row.created_at) : null;
          if (chatClearedAtRef.current && rowTime && rowTime <= chatClearedAtRef.current) continue;

          if (row.direction === 'in') {
            if (seenInboundIdsRef.current.has(id)) continue;
            seenInboundIdsRef.current.add(id);
            const cleanText = text
              .replace(/^\[button\]\s*/i, '')
              .replace(/\s*\([^)]+\)$/g, '')
              .trim();
            setChat(prev => {
              if (prev.some(m => m.direction === 'in' && m.text === cleanText)) return prev;
              return [...prev, {
                direction: 'in',
                text: cleanText,
                timestamp: row.created_at
                  ? new Date(row.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                  : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              }];
            });
          } else if (row.direction === 'out') {
            if (seenOutboundIdsRef.current.has(id)) continue;
            seenOutboundIdsRef.current.add(id);
            const cleanText = text.replace(/^\[(?:menu|template)\]\s*/i, '').trim();
            setChat(prev => {
              if (prev.some(m => m.direction === 'out' && m.text === cleanText)) return prev;
              return [...prev, {
                direction: 'out',
                text: cleanText,
                timestamp: row.created_at
                  ? new Date(row.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                  : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              }];
            });
          }
        }
      } catch (e) {
        console.error('Poll messages:', e);
      }
    }, 2500);
    return () => clearInterval(interval);
  }, [phone, headers]);

  const handleAction = useCallback(async (actionId, context = {}) => {
    if (!order) return;
    addUserMessage(ACTION_LABELS[actionId] || actionId);
    try {
      const res = await legacyApi.post(`/orders/${order.orderId}/actions`, {
        actionId,
        context: { ...context, orderId: order.orderId },
      }, { headers });
      const data = res.data;
      if (data.duplicate) return;
      addBotMessage(data.message, (data.actions || []).map(id => ({ id, title: ACTION_LABELS[id] || id })));
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  }, [order, addUserMessage, addBotMessage, headers]);

  const handleActionRef = useRef(handleAction);
  handleActionRef.current = handleAction;

  const handleSend = () => {
    if (isCounting) {
      setIsCounting(false);
      setCountdown(0);
      return;
    }
    if (!selectedProduct || !phone || !customerName || !consent) {
      toast.error('Please fill all fields and provide consent');
      return;
    }
    const useCountdown = source !== 'manual' && consent;
    if (useCountdown) {
      setIsCounting(true);
      setCountdown(3);
    } else {
      placeOrder();
    }
  };

  const placeOrder = async () => {
    if (!selectedProduct || !phone || !customerName || !consent) {
      toast.error('Please fill all fields and provide consent');
      return;
    }
    if (!isValidE164(phone)) {
      toast.error('Phone number must be in E.164 format (e.g. +91XXXXXXXXXX)');
      return;
    }
    setLoading(true);
    try {
      const idempotencyKey = generateUUID();
      const res = await legacyApi.post(`/orders/quick`, {
        phoneE164: phone,
        productId: selectedProduct.id,
        consent,
        idempotencyKey,
      }, { headers });
      const newOrder = res.data;
      setOrder(newOrder);
      setChat([]);
      const wa = newOrder.whatsapp || {};
      let waMsg = '';
      if (wa.templateSent && wa.buttonsSent) waMsg = ' WhatsApp confirmation sent (template + buttons).';
      else if (wa.templateSent) waMsg = ' WhatsApp template sent. Interactive buttons require 24h window.';
      else if (wa.buttonsSent) waMsg = ' WhatsApp confirmation sent with buttons.';
      else if (wa.hint) waMsg = ` WhatsApp not delivered: ${wa.hint}`;
      addBotMessage(
        `Order ${newOrder.orderRef} confirmed! Total: ${newOrder.totalAmount} ${newOrder.currency || 'INR'}.${waMsg} Select an option below.`,
        (newOrder.allowedActions || []).map(id => ({ id, title: ACTION_LABELS[id] || id }))
      );
      if (wa.templateSent || wa.buttonsSent) {
        toast.success('Order placed! WhatsApp sent automatically.');
      } else {
        toast.success('Order placed!');
        if (wa.hint) toast.error(wa.hint, { duration: 6000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
      setIsCounting(false);
      setCountdown(0);
    }
  };

  const resendConfirmation = async () => {
    if (!order) return;
    setLoading(true);
    try {
      const res = await legacyApi.post(`/orders/${order.orderRef}/resend-confirmation`, {}, { headers });
      const data = res.data;
      setOrder(prev => prev ? { ...prev, whatsapp: data.whatsapp } : null);
      if (data.whatsapp?.alreadySent || data.whatsapp?.templateSent || data.whatsapp?.buttonsSent) {
        toast.success('WhatsApp confirmation sent.');
      } else if (data.whatsapp?.hint) {
        toast.error(data.whatsapp.hint, { duration: 6000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (newStatus) => {
    if (!order) return;
    setStatusUpdateLoading(true);
    try {
      const res = await legacyApi.post(`/orders/${order.orderId}/status`, {
        status: newStatus,
      }, { headers });
      const updated = res.data;
      addBotMessage(
        `Order ${updated.orderRef} is now ${updated.status}. WhatsApp notification sent automatically.`,
        (updated.allowedActions || []).map(id => ({ id, title: ACTION_LABELS[id] || id }))
      );
      toast.success(`Status updated to ${newStatus}`);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const resetOrder = () => {
    setOrder(null);
    setChat([]);
    setSelectedProduct(null);
    setCustomerName('');
    setPhone('');
    setShippingAddress('');
    setConsent(false);
    setSource('manual');
    setAutoFillSource(null);
    setPendingProductId(null);
    setIsCounting(false);
    setCountdown(0);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="arcam-card bg-white rounded-xl border border-neutral-200 shadow-sm p-6">
        <h3 className="arcam-card-title text-lg font-bold text-neutral-900 mb-4 flex items-center gap-2">
          <FiShoppingBag className="w-5 h-5 arcam-gold-icon text-emerald-600" />
          Order Demo
          {vendor && <span className="text-xs font-normal text-neutral-400">· {vendor.name}</span>}
        </h3>

        {!order ? (
          <div className="space-y-4">
            <div>
              <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">Source</label>
              <div className="flex gap-2 mb-2">
                <button onClick={() => handleSourceChange('manual')} className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${source === 'manual' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50'}`}>Manual</button>
                <button onClick={() => handleSourceChange('customer')} className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${source === 'customer' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50'}`}>Customer</button>
                <button onClick={() => handleSourceChange('order')} className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${source === 'order' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50'}`}>Order</button>
              </div>
              {source !== 'manual' && (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {recordLoading ? (
                    <p className="text-xs text-neutral-400">Loading...</p>
                  ) : (
                    (source === 'customer' ? recentCustomers : recentOrders).map(rec => (
                      <div
                        key={rec.id}
                        onClick={() => handleSelectRecord(rec)}
                        className="flex items-center justify-between p-2 rounded border border-neutral-200 hover:bg-neutral-50 cursor-pointer text-sm"
                      >
                        <span className="font-medium text-neutral-900">{source === 'customer' ? rec.name : rec.order_ref}</span>
                        <span className="text-xs text-neutral-500">{source === 'customer' ? rec.phone_e164 : `${rec.customer_name} · ${rec.total_amount} ${rec.currency}`}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
              {autoFillSource && (
                <p className="text-xs text-emerald-600 mt-2">Auto-filled from {autoFillSource}. You can still edit below.</p>
              )}
            </div>

            <div>
              <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">Select Product</label>
              <div className="arcam-product-grid grid grid-cols-2 gap-2">
                {products.map(p => (
                  <button
                    key={p.id}
                    onClick={() => setSelectedProduct(p)}
                    className={`arcam-product-card p-3 rounded-lg border text-left transition-all border-neutral-200 hover:border-neutral-300 ${selectedProduct?.id === p.id ? 'arcam-product-card--selected border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200' : ''}`}
                  >
                    <p className="arcam-product-name font-semibold text-sm text-neutral-900">{p.name}</p>
                    <p className="arcam-product-sku text-xs text-neutral-500 mt-1">{p.sku}</p>
                    <p className="arcam-product-price text-sm font-bold text-emerald-600 mt-1">{p.price} {p.currency}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">Customer Name</label>
                <input type="text" value={customerName} onChange={e => setCustomerName(e.target.value)}
                  placeholder="John Doe"
                  className="arcam-input w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
              </div>
              <div>
                <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">WhatsApp Number</label>
                <input type="text" value={phone} onChange={e => setPhone(e.target.value)}
                  placeholder="+91XXXXXXXXXX"
                  className="arcam-input w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
              </div>
            </div>

            <div>
              <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">Shipping Address</label>
              <textarea value={shippingAddress} onChange={e => setShippingAddress(e.target.value)}
                placeholder="123 Main St, City, State, ZIP" rows={2}
                className="arcam-textarea w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)}
                className="arcam-checkbox w-4 h-4 rounded border-neutral-300 text-emerald-600 focus:ring-emerald-200" />
              <span className="text-sm text-neutral-600">I consent to receive order updates on WhatsApp</span>
            </label>

            {source !== 'manual' && selectedProduct && customerName && phone && consent && (
              <div className="bg-emerald-50 rounded-lg p-3 space-y-1">
                <p className="text-sm font-medium text-emerald-800">Review & Send</p>
                <p className="text-xs text-emerald-700">Product: {selectedProduct.name}</p>
                <p className="text-xs text-emerald-700">Customer: {customerName} · {phone}</p>
                {shippingAddress && <p className="text-xs text-emerald-700">Address: {shippingAddress}</p>}
                <p className="text-xs text-emerald-700">Consent: {consent ? 'Yes' : 'No'}</p>
              </div>
            )}

            <button onClick={handleSend}
              disabled={loading || !selectedProduct || !phone || !customerName || !consent}
              className="arcam-btn py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 w-full"
            >
              {loading ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiSend className="w-4 h-4" />}
              {isCounting ? `Send in ${countdown}... (Cancel)` : 'Send WhatsApp'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-neutral-500">Order Reference</p>
                <p className="font-bold text-neutral-900">{order.orderRef}</p>
              </div>
              <span className={`arcam-status-badge px-2 py-0.5 rounded-full text-xs font-semibold border arcam-status--${order.status} ${STATUS_COLORS[order.status]}`}>
                {order.status}
              </span>
            </div>

            <div className="bg-neutral-50 rounded-lg p-3 space-y-1">
              <p className="text-sm"><span className="text-neutral-500">Total:</span> {order.totalAmount} {order.currency}</p>
              {order.whatsapp?.templateSent && (
                <p className="text-xs text-emerald-600 flex items-center gap-1">
                  <FiCheckCircle className="w-3 h-3" /> WhatsApp template sent
                </p>
              )}
              {order.whatsapp?.buttonsSent && (
                <p className="text-xs text-emerald-600 flex items-center gap-1">
                  <FiCheckCircle className="w-3 h-3" /> WhatsApp interactive buttons sent
                </p>
              )}
              {order.whatsapp?.hint && !order.whatsapp?.templateSent && !order.whatsapp?.buttonsSent && (
                <p className="text-xs text-amber-600 flex items-start gap-1">
                  <FiMessageCircle className="w-3 h-3 flex-shrink-0 mt-0.5" /> {order.whatsapp.hint}
                </p>
              )}
            </div>

            {order.whatsapp?.hint && !order.whatsapp?.templateSent && !order.whatsapp?.buttonsSent && (
              <button onClick={resendConfirmation}
                disabled={loading}
                className="arcam-btn py-2.5 rounded-lg bg-amber-600 text-white font-semibold text-sm hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 w-full"
              >
                {loading ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiSend className="w-4 h-4" />}
                Resend WhatsApp
              </button>
            )}

            <div>
              <p className="arcam-label block text-xs font-medium text-neutral-600 mb-1">Demo Status Controls (triggers auto WhatsApp)</p>
              <div className="flex flex-wrap gap-2">
                {['CONFIRMED', 'PACKED', 'SHIPPED', 'DELIVERED', 'CANCELLED'].map(s => (
                  <button key={s} onClick={() => updateStatus(s)}
                    disabled={statusUpdateLoading || order.status === s}
                    className="arcam-btn py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 arcam-btn--outline px-3 py-1.5 rounded-lg text-xs font-medium border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 transition-colors arcam-btn--sm px-3 py-1.5 text-xs"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <button onClick={resetOrder}
              className="arcam-btn py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 arcam-btn--outline px-3 py-1.5 rounded-lg text-xs font-medium border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 transition-colors w-full"
            >
              Reset & Place New Order
            </button>
          </div>
        )}
      </div>

      <WhatsAppChatFragment vendor={vendor} phone={phone} />
    </div>
  );
}

function CampaignFlowTab({ vendor }) {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [phone, setPhone] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [chat, setChat] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [source, setSource] = useState('manual');
  const [recordLoading, setRecordLoading] = useState(false);
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [autoFillSource, setAutoFillSource] = useState(null);
  const chatEndRef = useRef(null);

  const headers = useMemo(() => vendor ? { 'x-vendor-api-key': vendor.api_key } : {}, [vendor]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat]);

  useEffect(() => {
    setLoadingProducts(true);
    legacyApi.get(`/products`, { headers })
      .then(res => { setProducts(res.data); setLoadingProducts(false); })
      .catch((e) => { setLoadingProducts(false); handleApiError(e); });
  }, [headers]);

  useEffect(() => {
    if (source === 'manual') {
      setRecentCustomers([]);
      return;
    }
    setRecordLoading(true);
    legacyApi.get(`/customers`, { headers })
      .then(res => setRecentCustomers(res.data))
      .catch(e => toast.error(e.response?.data?.error || e.message))
      .finally(() => setRecordLoading(false));
  }, [source, headers]);

  const handleSourceChange = (newSource) => {
    setSource(newSource);
    setAutoFillSource(null);
    setSelectedCustomer(null);
    setConsent(false);
    if (newSource === 'manual') {
      setCustomerName('');
      setPhone('');
    }
  };

  const handleSelectCustomer = async (customer) => {
    setRecordLoading(true);
    try {
      const res = await legacyApi.get(`/customers/${customer.id}`, { headers });
      const data = res.data;
      setCustomerName(data.name || '');
      setPhone(data.phone_e164 || '');
      setConsent(!!data.preference?.marketingOptIn);
      setSelectedCustomer(data);
      setAutoFillSource(`customer ${data.name || data.phone_e164}`);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setRecordLoading(false);
    }
  };

  const addBotMessage = (text, buttons = []) => {
    setChat(prev => [...prev, {
      direction: 'out',
      text,
      buttons: buttons.map(b => ({ ...b, onClick: () => handleCampaignAction(b.id) })),
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    }]);
  };

  const addUserMessage = (text) => {
    setChat(prev => [...prev, {
      direction: 'in',
      text,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    }]);
  };

  const handleCampaignAction = async (actionId) => {
    addUserMessage(ACTION_LABELS[actionId] || actionId);
    try {
      const res = await legacyApi.post(`/actions/handle`, {
        actionId,
        phoneE164: phone,
        context: { productId: selectedProduct?.id },
      }, { headers });
      const data = res.data;
      if (data.duplicate) return;
      addBotMessage(data.message, (data.actions || []).map(id => ({ id, title: ACTION_LABELS[id] || id })));
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const sendCampaign = async () => {
    if (!phone || !selectedProduct) {
      toast.error('Phone and product are required');
      return;
    }
    if (!isValidE164(phone)) {
      toast.error('Phone number must be in E.164 format (e.g. +91XXXXXXXXXX)');
      return;
    }
    if (!consent) {
      toast.error('Customer must consent to marketing');
      return;
    }
    setLoading(true);
    try {
      const idempotencyKey = generateUUID();
      const res = await v1Api.post(`/v1/campaigns/quick`, {
        customerId: selectedCustomer?.id,
        phoneE164: phone,
        productId: selectedProduct.id,
        templateName: templateName || undefined,
        consent,
        idempotencyKey,
      }, { headers });
      setCampaign(res.data);
      setChat([]);
      if (res.data.sent) {
        addBotMessage(
          `Campaign sent to ${customerName || 'Customer'}. Discover ${selectedProduct.name}. Select an option below.`,
          (res.data.allowedActions || []).map(id => ({ id, title: ACTION_LABELS[id] || id }))
        );
        toast.success('Campaign sent with interactive buttons!');
      } else {
        const hint = res.data.whatsapp?.hint || 'Campaign could not be delivered.';
        toast.error(hint, { duration: 6000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setCampaign(null);
    setChat([]);
    setSelectedProduct(null);
    setPhone('');
    setCustomerName('');
    setTemplateName('');
    setConsent(false);
    setSource('manual');
    setSelectedCustomer(null);
    setAutoFillSource(null);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="arcam-card bg-white rounded-xl border border-neutral-200 shadow-sm p-6">
        <h3 className="arcam-card-title text-lg font-bold text-neutral-900 mb-4 flex items-center gap-2">
          <FiTrendingUp className="w-5 h-5 arcam-gold-icon text-emerald-600" />
          Seasonal Campaign
          {vendor && <span className="text-xs font-normal text-neutral-400">· {vendor.name}</span>}
        </h3>

        {!campaign ? (
          <div className="space-y-4">
            <div>
              <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">Source</label>
              <div className="flex gap-2 mb-2">
                <button onClick={() => handleSourceChange('manual')} className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${source === 'manual' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50'}`}>Manual</button>
                <button onClick={() => handleSourceChange('customer')} className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${source === 'customer' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50'}`}>Customer</button>
              </div>
              {source === 'customer' && (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {recordLoading ? (
                    <p className="text-xs text-neutral-400">Loading...</p>
                  ) : (
                    recentCustomers.map(c => (
                      <div
                        key={c.id}
                        onClick={() => handleSelectCustomer(c)}
                        className="flex items-center justify-between p-2 rounded border border-neutral-200 hover:bg-neutral-50 cursor-pointer text-sm"
                      >
                        <span className="font-medium text-neutral-900">{c.name}</span>
                        <span className="text-xs text-neutral-500">{c.phone_e164}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
              {autoFillSource && (
                <p className="text-xs text-emerald-600 mt-2">Auto-filled from {autoFillSource}.</p>
              )}
            </div>

            <div>
              <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">Select Product</label>
              <div className="arcam-product-grid grid grid-cols-2 gap-2">
                {products.map(p => (
                  <button key={p.id} onClick={() => setSelectedProduct(p)}
                    className={`arcam-product-card p-3 rounded-lg border text-left transition-all border-neutral-200 hover:border-neutral-300 ${selectedProduct?.id === p.id ? 'arcam-product-card--selected border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200' : ''}`}
                  >
                    <p className="arcam-product-name font-semibold text-sm text-neutral-900">{p.name}</p>
                    <p className="arcam-product-price text-sm font-bold text-emerald-600 mt-1">{p.price} {p.currency}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">Customer Name</label>
                <input type="text" value={customerName} onChange={e => setCustomerName(e.target.value)}
                  placeholder="John Doe"
                  className="arcam-input w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
              </div>
              <div>
                <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">WhatsApp Number</label>
                <input type="text" value={phone} onChange={e => setPhone(e.target.value)}
                  placeholder="+91XXXXXXXXXX"
                  className="arcam-input w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
              </div>
            </div>

            <div>
              <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">Template Name (optional)</label>
              <input type="text" value={templateName} onChange={e => setTemplateName(e.target.value)}
                placeholder="seasonal_campaign_en"
                className="arcam-input w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)}
                className="w-4 h-4 rounded border-neutral-300 text-emerald-600 focus:ring-emerald-200" />
              <span className="text-sm text-neutral-600">Customer consents to marketing messages</span>
            </label>

            <button onClick={sendCampaign}
              disabled={loading || !selectedProduct || !phone || !consent}
              className="arcam-btn py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 w-full"
            >
              {loading ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiSend className="w-4 h-4" />}
              Send Campaign (Auto WhatsApp)
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-neutral-50 rounded-lg p-3">
              <p className="text-sm"><span className="text-neutral-500">Campaign Ref:</span> {campaign.campaignRef}</p>
              <p className="text-sm"><span className="text-neutral-500">Sent:</span> {campaign.sent ? 'Yes' : 'No'}</p>
            </div>
            <button onClick={reset}
              className="arcam-btn py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 arcam-btn--outline px-3 py-1.5 rounded-lg text-xs font-medium border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 transition-colors w-full"
            >
              Reset Campaign
            </button>
          </div>
        )}
      </div>

      <WhatsAppChatFragment vendor={vendor} phone={phone} />
    </div>
  );
}

function WholesaleFlowTab({ vendor, selectedRecord }) {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [phone, setPhone] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [productName, setProductName] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [deliveryCountry, setDeliveryCountry] = useState('');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [request, setRequest] = useState(null);
  const [quotation, setQuotation] = useState(null);
  const [chat, setChat] = useState([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [source, setSource] = useState('manual');
  const [recordLoading, setRecordLoading] = useState(false);
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [recentRequests, setRecentRequests] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [autoFillSource, setAutoFillSource] = useState(null);
  const chatEndRef = useRef(null);

  const headers = useMemo(() => vendor ? { 'x-vendor-api-key': vendor.api_key } : {}, [vendor]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat]);

  useEffect(() => {
    setLoadingProducts(true);
    legacyApi.get(`/products`, { headers })
      .then(res => { setProducts(res.data); setLoadingProducts(false); })
      .catch((e) => { setLoadingProducts(false); handleApiError(e); });
  }, [headers]);

  useEffect(() => {
    if (source === 'manual') {
      setRecentCustomers([]);
      setRecentRequests([]);
      return;
    }
    setRecordLoading(true);
    const endpoint = source === 'customer' ? '/customers' : '/wholesale/requests';
    const client = source === 'customer' ? legacyApi : v1Api;
    client.get(endpoint, { headers })
      .then(res => source === 'customer' ? setRecentCustomers(res.data) : setRecentRequests(res.data))
      .catch(e => toast.error(e.response?.data?.error || e.message))
      .finally(() => setRecordLoading(false));
  }, [source, headers]);

  const loadFromCustomer = (customer) => {
    setCustomerName(customer.name || '');
    setPhone(customer.phone_e164 || '');
    setSelectedCustomer(customer);
    setAutoFillSource(`customer ${customer.name || customer.phone_e164}`);
  };

  const loadFromRequest = (wsReq) => {
    setCustomerName(wsReq.customer_name || '');
    setPhone(wsReq.customer_phone || '');
    setCompanyName(wsReq.company_name || '');
    setProductName(wsReq.product_name || '');
    setQuantity(wsReq.quantity || 1);
    setDeliveryCountry(wsReq.delivery_country || '');
    setConsent(!!wsReq.consent);
    setSelectedCustomer(null);
    const product = products.find(p => p.name === wsReq.product_name);
    if (product) setSelectedProduct(product);
    setAutoFillSource(`wholesale request ${wsReq.request_ref}`);
  };

  const handleSourceChange = (newSource) => {
    setSource(newSource);
    setAutoFillSource(null);
    setSelectedCustomer(null);
    setRequest(null);
    setQuotation(null);
    setChat([]);
    if (newSource === 'manual') {
      setSelectedProduct(null);
      setCustomerName('');
      setPhone('');
      setCompanyName('');
      setProductName('');
      setQuantity(1);
      setDeliveryCountry('');
      setConsent(false);
    }
  };

  const handleSelectRecord = (record) => {
    if (record.type === 'customer' || record.phone_e164) {
      loadFromCustomer(record);
    } else {
      loadFromRequest(record);
    }
  };

  useEffect(() => {
    if (selectedRecord?.ref) {
      setSource(selectedRecord.type === 'wholesale' ? 'request' : 'customer');
      if (selectedRecord.type === 'wholesale') {
        v1Api.get(`/v1/wholesale/requests/${selectedRecord.ref}`, { headers })
          .then(res => loadFromRequest(res.data))
          .catch(e => toast.error(e.response?.data?.error || e.message));
      } else if (selectedRecord.id) {
        legacyApi.get(`/customers/${selectedRecord.id}`, { headers })
          .then(res => loadFromCustomer(res.data))
          .catch(e => toast.error(e.response?.data?.error || e.message));
      }
    }
  }, [selectedRecord]); // eslint-disable-line react-hooks/exhaustive-deps

  const addBotMessage = (text, buttons = []) => {
    setChat(prev => [...prev, {
      direction: 'out',
      text,
      buttons: buttons.map(b => ({ ...b, onClick: () => handleWholesaleAction(b.id) })),
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    }]);
  };

  const addUserMessage = (text) => {
    setChat(prev => [...prev, {
      direction: 'in',
      text,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    }]);
  };

  const handleWholesaleAction = async (actionId) => {
    addUserMessage(ACTION_LABELS[actionId] || actionId);
    try {
      const res = await legacyApi.post(`/actions/handle`, {
        actionId,
        phoneE164: phone,
        context: { requestId: request?.requestId, quotationId: quotation?.quotationId },
      }, { headers });
      const data = res.data;
      if (data.duplicate) return;
      addBotMessage(data.message, (data.actions || []).map(id => ({ id, title: ACTION_LABELS[id] || id })));
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const triggerRequest = async () => {
    if (!phone || !consent) {
      toast.error('WhatsApp number and consent are required');
      return;
    }
    if (phone && !isValidE164(phone)) {
      toast.error('Phone number must be in E.164 format (e.g. +91XXXXXXXXXX)');
      return;
    }
    setLoading(true);
    try {
      const res = await legacyApi.post(`/actions/trigger`, {
        actionId: 'wholesale_start_request',
        phoneE164: phone,
        context: { companyName },
      }, { headers });
      setChat([]);
      addUserMessage(ACTION_LABELS['wholesale_start_request'] || 'Request quotation');
      addBotMessage(res.data.message, (res.data.actions || []).map(id => ({ id, title: ACTION_LABELS[id] || id })));
      toast.success('Quotation request sent to WhatsApp.');
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const updateRequestStatus = async (newStatus) => {
    if (!request) return;
    setStatusLoading(true);
    try {
      await v1Api.post(`/v1/wholesale/requests/${request.requestId}/status`, { status: newStatus }, { headers });
      addBotMessage(`Request ${request.requestRef} status updated to: ${newStatus.replace(/_/g, ' ')}.`);
      toast.success(`Status updated to ${newStatus}`);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setStatusLoading(false);
    }
  };

  const createQuotation = async () => {
    if (!request) return;
    setStatusLoading(true);
    try {
      const totalAmount = selectedProduct ? selectedProduct.price * (quantity || 1) : 50000;
      const res = await v1Api.post(`/v1/quotations`, {
        requestId: request.requestId,
        totalAmount,
        currency: selectedProduct?.currency || 'INR',
        documentUrl: undefined,
        validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      }, { headers });
      setQuotation(res.data);
      addBotMessage(
        `Quotation ${res.data.quotationRef} (v${res.data.version}) is ready. Total: ${totalAmount} INR. WhatsApp sent automatically. Select an option below.`,
        [
          { id: 'quotation_view', title: 'View quotation' },
          { id: 'quotation_accept', title: 'Accept & proceed' },
          { id: 'quotation_request_changes', title: 'Request changes' },
        ]
      );
      toast.success('Quotation created! WhatsApp sent automatically.');
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setStatusLoading(false);
    }
  };

  const reset = () => {
    setRequest(null);
    setQuotation(null);
    setChat([]);
    setSelectedProduct(null);
    setCustomerName('');
    setPhone('');
    setCompanyName('');
    setProductName('');
    setQuantity(1);
    setDeliveryCountry('');
    setConsent(false);
    setSource('manual');
    setSelectedCustomer(null);
    setAutoFillSource(null);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="arcam-card bg-white rounded-xl border border-neutral-200 shadow-sm p-6">
        <h3 className="arcam-card-title text-lg font-bold text-neutral-900 mb-4 flex items-center gap-2">
          <FiDollarSign className="w-5 h-5 arcam-gold-icon text-emerald-600" />
          Wholesale & Quotation
          {vendor && <span className="text-xs font-normal text-neutral-400">· {vendor.name}</span>}
        </h3>

        {!request ? (
          <div className="space-y-4">
            <div>
              <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">Source</label>
              <div className="flex gap-2 mb-2">
                <button onClick={() => handleSourceChange('manual')} className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${source === 'manual' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50'}`}>Manual</button>
                <button onClick={() => handleSourceChange('customer')} className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${source === 'customer' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50'}`}>Customer</button>
                <button onClick={() => handleSourceChange('request')} className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${source === 'request' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50'}`}>Request</button>
              </div>
              {source !== 'manual' && (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {recordLoading ? (
                    <p className="text-xs text-neutral-400">Loading...</p>
                  ) : (
                    (source === 'customer' ? recentCustomers : recentRequests).map(rec => (
                      <div
                        key={rec.id}
                        onClick={() => handleSelectRecord(rec)}
                        className="flex items-center justify-between p-2 rounded border border-neutral-200 hover:bg-neutral-50 cursor-pointer text-sm"
                      >
                        <span className="font-medium text-neutral-900">{source === 'customer' ? rec.name : rec.request_ref}</span>
                        <span className="text-xs text-neutral-500">{source === 'customer' ? rec.phone_e164 : `${rec.customer_name} · ${rec.product_name}`}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
              {autoFillSource && (
                <p className="text-xs text-emerald-600 mt-2">Auto-filled from {autoFillSource}.</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">Company Name</label>
                <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)}
                  placeholder="Acme Corp"
                  className="arcam-input w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
              </div>
              <div>
                <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">WhatsApp Number</label>
                <input type="text" value={phone} onChange={e => setPhone(e.target.value)}
                  placeholder="+91XXXXXXXXXX"
                  className="arcam-input w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)}
                className="arcam-checkbox w-4 h-4 rounded border-neutral-300 text-emerald-600 focus:ring-emerald-200" />
              <span className="text-sm text-neutral-600">I consent to be contacted about this request</span>
            </label>

            <button onClick={triggerRequest}
              disabled={loading || !phone || !consent}
              className="arcam-btn py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 w-full"
            >
              {loading ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiSend className="w-4 h-4" />}
              Request Quotation (Auto WhatsApp)
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-neutral-50 rounded-lg p-3 space-y-1">
              <p className="text-sm"><span className="text-neutral-500">Request Ref:</span> {request.requestRef}</p>
              <p className="text-sm"><span className="text-neutral-500">Product:</span> {productName}</p>
              <p className="text-sm"><span className="text-neutral-500">Quantity:</span> {quantity}</p>
              <p className="text-sm"><span className="text-neutral-500">Status:</span> {request.status}</p>
              {quotation && (
                <>
                  <hr className="my-2 border-neutral-200" />
                  <p className="text-sm"><span className="text-neutral-500">Quotation Ref:</span> {quotation.quotationRef}</p>
                  <p className="text-sm"><span className="text-neutral-500">Version:</span> v{quotation.version}</p>
                </>
              )}
            </div>

            {!quotation ? (
              <div>
                <p className="arcam-label block text-xs font-medium text-neutral-600 mb-1">Demo Status Controls</p>
                <div className="flex flex-wrap gap-2">
                  {['UNDER_REVIEW', 'REVIEWED'].map(s => (
                    <button key={s} onClick={() => updateRequestStatus(s)} disabled={statusLoading}
                      className="arcam-btn py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 arcam-btn--outline px-3 py-1.5 rounded-lg text-xs font-medium border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 transition-colors arcam-btn--sm px-3 py-1.5 text-xs"
                    >
                      {s.replace(/_/g, ' ')}
                    </button>
                  ))}
                  <button onClick={createQuotation} disabled={statusLoading}
                    className="arcam-btn py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 arcam-btn--sm px-3 py-1.5 text-xs"
                  >
                    Create Quotation (Auto WhatsApp)
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <p className="arcam-label block text-xs font-medium text-neutral-600 mb-1">Quotation Actions</p>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => handleWholesaleAction('quotation_view')}
                    className="arcam-btn py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 arcam-btn--outline px-3 py-1.5 rounded-lg text-xs font-medium border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 transition-colors arcam-btn--sm px-3 py-1.5 text-xs"
                  >View Quotation</button>
                  <button onClick={() => handleWholesaleAction('quotation_accept')}
                    className="arcam-btn py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 arcam-btn--sm px-3 py-1.5 text-xs"
                  >Accept</button>
                  <button onClick={() => handleWholesaleAction('quotation_request_changes')}
                    className="arcam-btn py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 arcam-btn--outline px-3 py-1.5 rounded-lg text-xs font-medium border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 transition-colors arcam-btn--sm px-3 py-1.5 text-xs"
                  >Request Changes</button>
                </div>
              </div>
            )}

            <button onClick={reset}
              className="arcam-btn py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 arcam-btn--outline px-3 py-1.5 rounded-lg text-xs font-medium border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 transition-colors w-full"
            >
              Reset & New Request
            </button>
          </div>
        )}
      </div>

      <WhatsAppChatFragment vendor={vendor} phone={phone} />
    </div>
  );
}

function ProductsTab({ vendor, onProductsChanged }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ sku: '', name: '', description: '', price: '', currency: 'INR', imageUrl: '' });
  const [seedProducts, setSeedProducts] = useState('');
  const [showSeed, setShowSeed] = useState(false);

  const headers = useMemo(() => vendor ? { 'x-vendor-api-key': vendor.api_key } : {}, [vendor]);

  const loadProducts = useCallback(async () => {
    if (!vendor) { setProducts([]); setLoading(false); setLoadingProducts(false); return; }
    setLoading(true);
    setLoadingProducts(true);
    try {
      const res = await legacyApi.get(`/products`, { headers });
      setProducts(res.data);
    } catch (e) {
      handleApiError(e);
    } finally {
      setLoading(false);
      setLoadingProducts(false);
    }
  }, [vendor, headers]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const handleAdd = async () => {
    if (!form.sku || !form.name || !form.price) {
      toast.error('SKU, name, and price are required');
      return;
    }
    try {
      await legacyApi.post(`/products`, {
        sku: form.sku, name: form.name, description: form.description,
        price: parseFloat(form.price), currency: form.currency, imageUrl: form.imageUrl || undefined,
      }, { headers });
      toast.success('Product added!');
      setForm({ sku: '', name: '', description: '', price: '', currency: 'INR', imageUrl: '' });
      setShowAdd(false);
      loadProducts();
      onProductsChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const handleEdit = async () => {
    if (!form.name || !form.price) {
      toast.error('Name and price are required');
      return;
    }
    try {
      await legacyApi.put(`/products/${editingId}`, {
        name: form.name, description: form.description,
        price: parseFloat(form.price), currency: form.currency, imageUrl: form.imageUrl || undefined,
      }, { headers });
      toast.success('Product updated!');
      setEditingId(null);
      setForm({ sku: '', name: '', description: '', price: '', currency: 'INR', imageUrl: '' });
      loadProducts();
      onProductsChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Deactivate this product? It will no longer appear in flows.')) return;
    try {
      await legacyApi.delete(`/products/${productId}`, { headers });
      toast.success('Product deactivated');
      loadProducts();
      onProductsChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const handleSeed = async () => {
    let customProducts = null;
    if (seedProducts.trim()) {
      try {
        customProducts = JSON.parse(seedProducts);
        if (!Array.isArray(customProducts)) throw new Error('Must be an array');
      } catch (e) {
        toast.error('Invalid JSON: ' + e.message);
        return;
      }
    }
    try {
      const res = await legacyApi.post(`/seed`, { products: customProducts, force: true }, { headers });
      toast.success(res.data.message);
      setShowSeed(false);
      setSeedProducts('');
      loadProducts();
      onProductsChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  if (!vendor) {
    return <div className="arcam-card bg-white rounded-xl border border-neutral-200 shadow-sm p-6 text-center text-neutral-400 p-8">Select a vendor to manage products.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="arcam-card bg-white rounded-xl border border-neutral-200 shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="arcam-card-title text-lg font-bold text-neutral-900 mb-4 flex items-center gap-2">
            <FiBox className="w-5 h-5 arcam-gold-icon text-emerald-600" />
            Products Inventory
            <span className="text-xs font-normal text-neutral-400">· {vendor.name}</span>
          </h3>
          <div className="flex gap-2">
            <button onClick={() => setShowSeed(!showSeed)}
              className="arcam-btn py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 arcam-btn--outline px-3 py-1.5 rounded-lg text-xs font-medium border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 transition-colors arcam-btn--sm px-3 py-1.5 text-xs"
            >
              <FiRefreshCw className="w-3 h-3" /> Seed Products
            </button>
            <button onClick={() => { setShowAdd(!showAdd); setEditingId(null); setForm({ sku: '', name: '', description: '', price: '', currency: 'INR', imageUrl: '' }); }}
              className="arcam-btn py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 arcam-btn--sm px-3 py-1.5 text-xs"
            >
              <FiPlus className="w-3 h-3" /> Add Product
            </button>
          </div>
        </div>

        {showSeed && (
          <div className="mb-4 p-4 bg-neutral-50 rounded-lg border border-neutral-200">
            <p className="text-sm font-medium text-neutral-700 mb-2">Seed Products</p>
            <p className="text-xs text-neutral-500 mb-2">Paste a JSON array of products, or leave empty to seed defaults. Existing active products will be deactivated.</p>
            <textarea
              value={seedProducts}
              onChange={e => setSeedProducts(e.target.value)}
              placeholder={'[{"sku":"P1","name":"Product 1","price":999,"currency":"INR","description":"Desc"}]'}
              rows={5}
              className="arcam-textarea w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
              style={{ fontFamily: 'monospace' }}
            />
            <div className="flex gap-2 mt-2">
              <button onClick={handleSeed}
                className="arcam-btn py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 arcam-btn--sm px-3 py-1.5 text-xs"
              >Seed Now</button>
              <button onClick={() => setShowSeed(false)}
                className="arcam-btn py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 arcam-btn--outline px-3 py-1.5 rounded-lg text-xs font-medium border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 transition-colors arcam-btn--sm px-3 py-1.5 text-xs"
              >Cancel</button>
            </div>
          </div>
        )}

        {(showAdd || editingId) && (
          <div className="mb-4 p-4 bg-neutral-50 rounded-lg border border-neutral-200">
            <p className="text-sm font-medium text-neutral-700 mb-3">{editingId ? 'Edit Product' : 'Add New Product'}</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">SKU</label>
                <input type="text" value={form.sku} disabled={!!editingId}
                  onChange={e => setForm({ ...form, sku: e.target.value })}
                  placeholder="UNIQUE-SKU"
                  className="arcam-input w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:bg-neutral-100" />
              </div>
              <div>
                <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">Name</label>
                <input type="text" value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="Product Name"
                  className="arcam-input w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
              </div>
              <div>
                <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">Price</label>
                <input type="number" value={form.price}
                  onChange={e => setForm({ ...form, price: e.target.value })}
                  placeholder="999"
                  className="arcam-input w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
              </div>
              <div>
                <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">Currency</label>
                <select value={form.currency}
                  onChange={e => setForm({ ...form, currency: e.target.value })}
                  className="arcam-select w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                >
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">Description</label>
                <input type="text" value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Product description"
                  className="arcam-input w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">Image URL (optional)</label>
                <input type="text" value={form.imageUrl}
                  onChange={e => setForm({ ...form, imageUrl: e.target.value })}
                  placeholder="https://..."
                  className="arcam-input w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={editingId ? handleEdit : handleAdd}
                className="arcam-btn py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 arcam-btn--sm px-3 py-1.5 text-xs"
              >{editingId ? 'Update' : 'Add Product'}</button>
              <button onClick={() => { setShowAdd(false); setEditingId(null); setForm({ sku: '', name: '', description: '', price: '', currency: 'INR', imageUrl: '' }); }}
                className="arcam-btn py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 arcam-btn--outline px-3 py-1.5 rounded-lg text-xs font-medium border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 transition-colors arcam-btn--sm px-3 py-1.5 text-xs"
              >Cancel</button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-neutral-400 text-center py-8">Loading products...</p>
        ) : products.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-neutral-400 mb-3">No products yet. Add products or seed demo data.</p>
            <button onClick={() => setShowSeed(true)}
              className="arcam-btn py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >Seed Demo Products</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="arcam-table w-full text-sm">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Name</th>
                  <th>Description</th>
                  <th className="text-right">Price</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr key={p.id}>
                    <td className="font-mono text-xs text-neutral-600">{p.sku}</td>
                    <td className="font-medium text-neutral-900">{p.name}</td>
                    <td className="text-neutral-500 text-xs max-w-xs truncate">{p.description}</td>
                    <td className="text-right font-semibold" style={{ color: 'var(--arcam-gold)' }}>{p.price} {p.currency}</td>
                    <td>
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => {
                          setEditingId(p.id);
                          setShowAdd(false);
                          setForm({ sku: p.sku, name: p.name, description: p.description || '', price: String(p.price), currency: p.currency, imageUrl: p.image_url || '' });
                        }}
                          className="p-1.5 rounded-lg text-neutral-400 hover:text-black hover:bg-neutral-100"
                        ><FiEdit2 className="w-3.5 h-3.5" /></button>
                        <button onClick={() => handleDelete(p.id)}
                          className="p-1.5 rounded-lg text-neutral-400 hover:text-red-600 hover:bg-red-50"
                        ><FiTrash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function WhatsAppTestTab({ vendor }) {
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [selectedProductId, setSelectedProductId] = useState('');
  const [products, setProducts] = useState([]);
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const [sentLog, setSentLog] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const headers = useMemo(() => vendor ? { 'x-vendor-api-key': vendor.api_key } : {}, [vendor]);

  useEffect(() => {
    if (!vendor) return;
    legacyApi.get(`/whatsapp/status`, { headers }).then(res => setStatus(res.data)).catch(handleApiError);
    setLoadingProducts(true);
    legacyApi.get(`/products`, { headers })
      .then(res => { setProducts(res.data); setLoadingProducts(false); })
      .catch((e) => { setLoadingProducts(false); handleApiError(e); });
  }, [vendor, headers]);

  const handleSend = async () => {
    if (!phone.trim()) {
      toast.error('Phone number is required (E.164 format, e.g. +91XXXXXXXXXX)');
      return;
    }
    if (!isValidE164(phone.trim())) {
      toast.error('Phone number must be in E.164 format (e.g. +91XXXXXXXXXX)');
      return;
    }
    setSending(true);
    try {
      const body = { phoneE164: phone.trim() };
      if (selectedProductId) body.productId = parseInt(selectedProductId, 10);
      if (message.trim()) body.message = message.trim();
      const res = await legacyApi.post(`/whatsapp/test`, body, { headers });
      toast.success(res.data.message || 'Message sent!');
      setSentLog(prev => [{
        timestamp: new Date().toLocaleTimeString(),
        phone: phone.trim(),
        productId: selectedProductId || null,
        waMessageId: res.data.waMessageId,
        success: true,
      }, ...prev]);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
      setSentLog(prev => [{
        timestamp: new Date().toLocaleTimeString(),
        phone: phone.trim(),
        productId: selectedProductId || null,
        error: e.response?.data?.error || e.message,
        success: false,
      }, ...prev]);
    } finally {
      setSending(false);
    }
  };

  if (!vendor) {
    return <div className="arcam-card bg-white rounded-xl border border-neutral-200 shadow-sm p-6 text-center text-neutral-400 p-8">Login to test WhatsApp.</div>;
  }

  return (
    <div className="space-y-4">
      {/* Status card */}
      <div className="arcam-card bg-white rounded-xl border border-neutral-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-2">
          <FiMessageCircle className="w-5 h-5" style={{ color: 'var(--arcam-gold)' }} />
          <h3 className="arcam-card-title text-lg font-bold text-neutral-900 mb-4 flex items-center gap-2" style={{ marginBottom: 0 }}>WhatsApp Test Panel</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
            status?.configured
              ? 'bg-emerald-100 text-emerald-700'
              : 'bg-red-100 text-red-700'
          }`}>
            <span className={`w-2 h-2 rounded-full ${status?.configured ? 'bg-emerald-500' : 'bg-red-500'}`} />
            {status?.configured ? 'WhatsApp Connected' : 'WhatsApp Not Configured'}
          </span>
          {status?.configured && (
            <span className="text-xs text-neutral-500">
              Messages will be sent from your configured WhatsApp Business number
            </span>
          )}
        </div>
        {!status?.configured && (
          <div className="mt-3 arcam-alert p-3 rounded-lg arcam-alert--warning bg-amber-50 border border-amber-200 text-amber-700">
            <p className="text-sm">
              WhatsApp is not configured on the server. The platform admin needs to set
              <code className="mx-1 px-1 py-0.5 bg-amber-100 rounded text-xs">WHATSAPP_ACCESS_TOKEN</code>
              and
              <code className="mx-1 px-1 py-0.5 bg-amber-100 rounded text-xs">WHATSAPP_PHONE_NUMBER_ID</code>
              in the server .env file.
            </p>
          </div>
        )}
      </div>

      {/* Send form */}
      <div className="arcam-card bg-white rounded-xl border border-neutral-200 shadow-sm p-6">
        <h4 className="arcam-card-title text-lg font-bold text-neutral-900 mb-4 flex items-center gap-2">Send a Test Message</h4>
        <div className="space-y-4">
          <div>
            <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">Customer Phone (E.164 format)</label>
            <input
              type="text"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="+91XXXXXXXXXX"
              className="arcam-input w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
            />
            <p className="text-xs text-neutral-400 mt-1">Use the full international format with country code</p>
          </div>

          <div>
            <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">Product (optional - sends interactive buttons)</label>
            <select
              value={selectedProductId}
              onChange={e => setSelectedProductId(e.target.value)}
              className="arcam-select w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
            >
              <option value="">— Plain text message —</option>
              {products.map(p => (
                <option key={p.id} value={p.id}>{p.name} ({p.sku}) - {p.price} {p.currency}</option>
              ))}
            </select>
            {selectedProductId && (
              <p className="text-xs mt-1" style={{ color: 'var(--arcam-gold)' }}>Will send product info with "Order Now", "Need Help", "Talk to Agent" buttons</p>
            )}
          </div>

          {!selectedProductId && (
            <div>
              <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">Custom Message (optional)</label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder={`Hello from ${vendor.name}! This is a test message.`}
                rows={3}
                className="arcam-textarea w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
              />
            </div>
          )}

          <button
            onClick={handleSend}
            disabled={sending || !status?.configured}
            className="arcam-btn py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {sending ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiSend className="w-4 h-4" />}
            Send WhatsApp Message
          </button>
        </div>
      </div>

      {/* Sent log */}
      {sentLog.length > 0 && (
        <div className="arcam-card bg-white rounded-xl border border-neutral-200 shadow-sm p-6">
          <h4 className="arcam-card-title text-lg font-bold text-neutral-900 mb-4 flex items-center gap-2">Recent Test Messages</h4>
          <div className="space-y-2">
            {sentLog.map((log, i) => (
              <div key={i} className={`p-3 rounded-lg border text-sm ${
                log.success ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-neutral-900">{log.phone}</span>
                  <span className="text-xs text-neutral-400">{log.timestamp}</span>
                </div>
                {log.productId && <span className="text-xs text-neutral-500">Product ID: {log.productId}</span>}
                {log.waMessageId && <span className="text-xs text-emerald-600 block">WA Message ID: {log.waMessageId}</span>}
                {log.error && <span className="text-xs text-red-600 block">Error: {log.error}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* How it works */}
      <div className="arcam-card bg-white rounded-xl border border-neutral-200 shadow-sm p-6">
        <h4 className="arcam-card-title text-lg font-bold text-neutral-900 mb-4 flex items-center gap-2">How the WhatsApp Ecommerce Flow Works</h4>
        <div className="space-y-3 text-sm text-neutral-600">
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: 'var(--arcam-gold)', color: 'var(--arcam-black)' }}>1</span>
            <p><strong>Customer messages your WhatsApp number</strong> — they receive an auto-menu with "Track Order", "FAQ / Help", and "Chat with agent" buttons</p>
          </div>
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: 'var(--arcam-gold)', color: 'var(--arcam-black)' }}>2</span>
            <p><strong>Customer taps "Track Order"</strong> — the system looks up their recent orders and shows order details with action buttons (View, Cancel, Need help)</p>
          </div>
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: 'var(--arcam-gold)', color: 'var(--arcam-black)' }}>3</span>
            <p><strong>Vendor places an order</strong> (via portal or API) — WhatsApp confirmation is sent to the customer automatically. If an approved template is configured, it sends as a template (works outside 24h window). Otherwise, interactive buttons are sent (only works if customer has messaged within 24h).</p>
          </div>
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: 'var(--arcam-gold)', color: 'var(--arcam-black)' }}>4</span>
            <p><strong>Vendor updates order status</strong> (PACKED, SHIPPED, DELIVERED) — automated WhatsApp notification sent to customer with relevant action buttons</p>
          </div>
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: 'var(--arcam-gold)', color: 'var(--arcam-black)' }}>5</span>
            <p><strong>Vendor sends campaigns</strong> — product promotional messages with buttons go out via WhatsApp. Campaigns require an approved template to send the first message.</p>
          </div>
        </div>

        <div className="mt-4 arcam-alert p-3 rounded-lg arcam-alert--warning bg-amber-50 border border-amber-200 text-amber-700">
          <h5 className="text-sm font-bold mb-2">Important: WhatsApp 24-Hour Window & Templates</h5>
          <ul className="space-y-1 text-xs list-disc list-inside">
            <li><strong>Interactive buttons & session text</strong> only work if the customer has messaged your WhatsApp number within the last 24 hours</li>
            <li><strong>Templates</strong> can be sent anytime (even outside 24h window) but must be pre-approved by Meta</li>
            <li>To send the first message to a new customer, you <strong>must</strong> use an approved template</li>
            <li>Go to <strong>Settings</strong> tab to configure template names for each message type</li>
            <li>Create and submit templates in Meta Business Manager or via the WhatsApp Templates page</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function VendorSettingsTab({ vendor, onUpdated }) {
  const DEFAULT_TEMPLATES = {
    orderConfirmationTemplate: 'ecom_order_confirmation',
    orderPackedTemplate: 'ecom_order_packed',
    orderShippedTemplate: 'ecom_order_shipped',
    orderDeliveredTemplate: 'ecom_order_delivered',
    campaignTemplate: 'ecom_campaign',
    wholesaleStartTemplate: 'ecom_wholesale_request',
    quotationReadyTemplate: 'quotation_ready_en',
  };

  const [config, setConfig] = useState({ ...DEFAULT_TEMPLATES });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!vendor) return;
    try {
      const parsed = vendor.whatsapp_config_json ? JSON.parse(vendor.whatsapp_config_json) : {};
      const overrides = {};
      Object.keys(DEFAULT_TEMPLATES).forEach((key) => {
        if (parsed[key]) overrides[key] = parsed[key];
      });
      setConfig({ ...DEFAULT_TEMPLATES, ...overrides });
    } catch (e) {}
  }, [vendor]);

  if (!vendor) {
    return <div className="arcam-card bg-white rounded-xl border border-neutral-200 shadow-sm p-6 text-center text-neutral-400 p-8">Select a vendor to configure settings.</div>;
  }

  const handleSave = async () => {
    setSaving(true);
    try {
      await legacyApi.put(`/vendors/${vendor.id}`, {
        whatsappConfig: config,
      });
      toast.success('Vendor settings saved!');
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { key: 'orderConfirmationTemplate', label: 'Order Confirmation Template', placeholder: 'ecom_order_confirmation' },
    { key: 'orderPackedTemplate', label: 'Order Packed Template', placeholder: 'ecom_order_packed' },
    { key: 'orderShippedTemplate', label: 'Order Shipped Template', placeholder: 'ecom_order_shipped' },
    { key: 'orderDeliveredTemplate', label: 'Order Delivered Template', placeholder: 'ecom_order_delivered' },
    { key: 'campaignTemplate', label: 'Campaign Template', placeholder: 'ecom_campaign' },
    { key: 'wholesaleStartTemplate', label: 'Wholesale Start Template', placeholder: 'ecom_wholesale_request' },
    { key: 'quotationReadyTemplate', label: 'Quotation Ready Template', placeholder: 'quotation_ready_en' },
  ];

  return (
    <div className="space-y-4">
      <div className="arcam-card bg-white rounded-xl border border-neutral-200 shadow-sm p-6">
        <h3 className="arcam-card-title text-lg font-bold text-neutral-900 mb-4 flex items-center gap-2">
          <FiSettings className="w-5 h-5 arcam-gold-icon text-emerald-600" />
          Vendor Settings
          <span className="text-xs font-normal text-neutral-400">· {vendor.name}</span>
        </h3>
        <p className="text-sm text-neutral-500 mb-4">Configure WhatsApp template names for automated messages. Leave blank to use env defaults.</p>

        <div className="bg-neutral-50 rounded-lg p-3 mb-4">
          <p className="text-xs text-neutral-500">Vendor API Key</p>
          <p className="text-sm font-mono text-neutral-900 break-all">{vendor.api_key}</p>
        </div>

        <div className="space-y-3">
          {fields.map(f => (
            <div key={f.key}>
              <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">{f.label}</label>
              <input type="text" value={config[f.key]}
                onChange={e => setConfig({ ...config, [f.key]: e.target.value })}
                placeholder={f.placeholder}
                className="arcam-input w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
              />
            </div>
          ))}
        </div>

        <button onClick={handleSave} disabled={saving}
          className="arcam-btn py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 mt-4"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}

function CustomersTab({ vendor }) {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', defaultShippingAddress: '', marketingOptIn: false });

  const headers = useMemo(() => vendor ? { 'x-vendor-api-key': vendor.api_key } : {}, [vendor]);

  useEffect(() => {
    setLoading(true);
    legacyApi.get(`/customers`, { headers })
      .then(res => setCustomers(res.data))
      .catch(e => toast.error(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [headers]);

  const selectCustomer = (customer) => {
    setLoading(true);
    legacyApi.get(`/customers/${customer.id}`, { headers })
      .then(res => {
        const data = res.data;
        setSelectedCustomer(data);
        setForm({
          name: data.name || '',
          defaultShippingAddress: data.defaultShippingAddress || '',
          marketingOptIn: !!data.preference?.marketingOptIn,
        });
      })
      .catch(e => toast.error(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };

  const saveCustomer = async () => {
    if (!selectedCustomer) return;
    setSaving(true);
    try {
      const res = await legacyApi.put(`/customers/${selectedCustomer.id}`, {
        name: form.name,
        defaultShippingAddress: form.defaultShippingAddress,
        marketingOptIn: form.marketingOptIn,
      }, { headers });
      setSelectedCustomer(res.data);
      setCustomers(prev => prev.map(c => c.id === res.data.id ? res.data : c));
      toast.success('Customer profile saved.');
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="arcam-card bg-white rounded-xl border border-neutral-200 shadow-sm p-6 lg:col-span-1">
        <h3 className="arcam-card-title text-lg font-bold text-neutral-900 mb-4 flex items-center gap-2">
          <FiUser className="w-5 h-5 text-emerald-600" />
          Customers
          {vendor && <span className="text-xs font-normal text-neutral-400">· {vendor.name}</span>}
        </h3>
        {loading && !customers.length ? (
          <p className="text-sm text-neutral-400">Loading...</p>
        ) : (
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {customers.map(c => (
              <div
                key={c.id}
                onClick={() => selectCustomer(c)}
                className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer text-sm transition-colors ${selectedCustomer?.id === c.id ? 'bg-emerald-50 border-emerald-500' : 'border-neutral-200 hover:bg-neutral-50'}`}
              >
                <span className="font-medium text-neutral-900">{c.name || 'Unnamed'}</span>
                <span className="text-xs text-neutral-500">{c.phone_e164}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="arcam-card bg-white rounded-xl border border-neutral-200 shadow-sm p-6 lg:col-span-2">
        {selectedCustomer ? (
          <div className="space-y-4">
            <h3 className="arcam-card-title text-lg font-bold text-neutral-900 mb-2">
              {selectedCustomer.name || 'Unnamed'} · {selectedCustomer.phone_e164}
            </h3>
            <div>
              <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">Name</label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="arcam-input w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
            </div>
            <div>
              <label className="arcam-label block text-xs font-medium text-neutral-600 mb-1">Default Shipping Address</label>
              <textarea value={form.defaultShippingAddress} onChange={e => setForm(f => ({ ...f, defaultShippingAddress: e.target.value }))}
                rows={4}
                className="arcam-input w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.marketingOptIn} onChange={e => setForm(f => ({ ...f, marketingOptIn: e.target.checked }))}
                className="arcam-checkbox w-4 h-4 rounded border-neutral-300 text-emerald-600 focus:ring-emerald-200" />
              <span className="text-sm text-neutral-600">Marketing opt-in (WhatsApp campaigns)</span>
            </label>
            <button onClick={saveCustomer} disabled={saving}
              className="arcam-btn py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {saving ? 'Saving...' : 'Save Customer'}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-neutral-400 text-sm">
            Select a customer to edit their profile.
          </div>
        )}
      </div>
    </div>
  );
}

const ARCAM_VENDOR_NAME = 'arcam';

export default function EcommerceFlow() {
  const [vendor, setVendor] = useState(null);
  const [activeTab, setActiveTab] = useState('products');
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const handleSelectRecord = useCallback((record) => {
    setSelectedRecord(record);
    setActiveTab(record.type);
  }, []);

  const isArcam = useMemo(() => vendor?.name?.trim().toLowerCase() === ARCAM_VENDOR_NAME, [vendor]);

  const handleLogin = (vendorData) => {
    const safe = { id: vendorData.id, api_key: vendorData.api_key, name: vendorData.name };
    localStorage.setItem('ecommerce_vendor', JSON.stringify(safe));
    setVendor(safe);
  };

  const handleLogout = () => {
    localStorage.removeItem('ecommerce_vendor');
    setVendor(null);
    setActiveTab('products');
  };

  const handleVendorUpdated = async () => {
    try {
      const res = await legacyApi.post(`/vendor/login`, { apiKey: vendor.api_key });
      const safe = { id: res.data.id, api_key: res.data.api_key, name: res.data.name };
      setVendor(safe);
      localStorage.setItem('ecommerce_vendor', JSON.stringify(safe));
    } catch (e) {
      handleApiError(e);
    }
  };

  if (!vendor) {
    return <VendorLogin onLogin={handleLogin} />;
  }

  const tabs = [
    { id: 'products', label: 'Products', icon: FiBox },
    { id: 'order', label: 'Order Flow', icon: FiShoppingBag },
    { id: 'campaign', label: 'Campaign Flow', icon: FiTrendingUp },
    { id: 'wholesale', label: 'Wholesale', icon: FiDollarSign },
    { id: 'customers', label: 'Customers', icon: FiUser },
    { id: 'whatsapp', label: 'WhatsApp Test', icon: FiMessageCircle },
    { id: 'dashboard', label: 'Dashboard', icon: FiBarChart },
    { id: 'settings', label: 'Settings', icon: FiSettings },
  ];

  return (
    <div className={isArcam ? 'arcam-theme' : 'min-h-screen bg-neutral-50'}>
      {/* Arcam-style header bar */}
      <div className={isArcam ? 'arcam-header' : 'mb-6 flex items-center justify-between'}>
        <div className="w-full max-w-[1850px] mx-auto px-6 lg:px-10 flex items-center justify-between">
          <div>
            <h1 className={isArcam ? '' : 'text-2xl font-bold text-neutral-900'}>{vendor.name}</h1>
            <p className={isArcam ? '' : 'text-sm text-neutral-500 mt-1'}>WhatsApp Ecommerce Portal</p>
          </div>
          <div className="flex items-center gap-3">
            {false && (
              <Link to="/" className={isArcam ? 'arcam-back-btn' : 'flex items-center gap-2 px-3 py-1.5 rounded-lg border border-neutral-200 bg-white text-sm font-semibold text-neutral-700 hover:bg-neutral-50 transition-colors shadow-sm'}>
                <FiArrowLeft className="w-4 h-4" />
                WhatsApp
              </Link>
            )}
            <button onClick={handleLogout} className={isArcam ? 'arcam-logout-btn' : 'flex items-center gap-2 px-3 py-1.5 rounded-lg border border-red-200 bg-white text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors shadow-sm'}>
              <FiXCircle className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="w-full max-w-[1850px] mx-auto px-6 lg:px-10 py-6">
        <div className={isArcam ? 'arcam-tabs mb-6' : 'flex gap-1 mb-6 bg-white rounded-xl border border-neutral-200 p-1 shadow-sm'}>
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={isArcam ? `arcam-tab ${activeTab === tab.id ? 'arcam-tab--active' : ''}` : `flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-emerald-600 text-white' : 'text-neutral-600 hover:bg-neutral-50'}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {activeTab === 'products' && <ProductsTab vendor={vendor} onProductsChanged={() => setRefreshKey(k => k + 1)} />}
        {activeTab === 'order' && <OrderFlowTab key={refreshKey} vendor={vendor} selectedRecord={selectedRecord} />}
        {activeTab === 'campaign' && <CampaignFlowTab key={refreshKey} vendor={vendor} />}
        {activeTab === 'wholesale' && <WholesaleFlowTab key={refreshKey} vendor={vendor} selectedRecord={selectedRecord} />}
        {activeTab === 'customers' && <CustomersTab vendor={vendor} />}
        {activeTab === 'whatsapp' && <WhatsAppTestTab vendor={vendor} />}
        {activeTab === 'dashboard' && <DashboardTab vendor={vendor} onSelectRecord={handleSelectRecord} />}
        {activeTab === 'settings' && <VendorSettingsTab vendor={vendor} onUpdated={handleVendorUpdated} />}
      </div>

      {/* Arcam-style footer */}
      <div className={isArcam ? 'arcam-footer' : 'hidden'}>
        <p className="arcam-footer-text text-xs text-center text-neutral-400">
          <span className="arcam-footer-gold">ARCAM</span> &nbsp;|&nbsp; WhatsApp Ecommerce Portal
        </p>
      </div>
    </div>
  );
}
